
import _ from 'lodash'
import { buildVideoSources } from '~/utils/videoUtils'

export default {
  name: 'DaisoVideo',
  props: {
    videoRef: {
      type: String,
      default: ''
    },
    alt: {
      type: String,
      default: ''
    },
    src: {
      type: String,
      default: ''
    },
    resize: {
      type: [Number, String],
      default: null
    },
    useOriginal: {
      type: Boolean,
      default: false
    },
    options: {
      type: Object,
      default: () => {
        return {
          playButton: true
        }
      },
    },
    fastStart: {
      type: Boolean,
      default: false
    },
    videoClass: {
      type: String,
      default: ''
    },
    trimRange: {
      type: String,
      default: null
    },
    pauseVideo: {
      type: Function,
      default: () => {}
    },
    useMouseEvent: {
      type: Boolean,
      default: false
    },
    isActive: {
      type: Boolean,
      default: false
    },
    useActiveControl: {
      type: Boolean,
      default: false
    },
    shouldLoop: {
      type: Boolean,
      default: false
    },
    useClickCatcher: {
      type: Boolean,
      default: false,
    },
    onVideoClick: {
      type: Function,
      default: null,
    },
  },
  data () {
    let hasMalformedPattern = false
    let safeSrc = ''

    if (typeof this.src === 'string') {
      safeSrc = this.src
      hasMalformedPattern = this.src && (this.src?.includes('//dims') || this.src?.includes('//xcdr'))
    }

    if (hasMalformedPattern) {
      this.$nextTick(() => {
        this.$emit('error')
      })
    }

    // data()에서 computed보다 먼저 실행되므로 effectiveResize 로직을 직접 계산
    let initialResize = null
    if (!this.useOriginal) {
      if (this.resize !== null) {
        initialResize = this.resize
      } else if (!(safeSrc && safeSrc.includes('/resize'))) {
        initialResize = 480
      }
    }

    const allSources = hasMalformedPattern || !safeSrc
      ? []
      : buildVideoSources(safeSrc, initialResize, this.fastStart, this.trimRange)

    return {
      videoOptions: {
        fluid: this.options.fluid ?? undefined,
        aspectRatio: this.options.aspectRatio || undefined,
        muted: this.options.muted ?? null,
        loop: this.options.loop,
        playsinline: this.options.playsinline,
        playbackRates: this.options.playbackRates,
        bigPlayButton: this.options.playButton,
        preload: this.options.preload || 'none',
        poster: this.options.poster,
        autoplay: hasMalformedPattern ? false : this.options.autoplay,
        controls: this.options.controls,
        // DOM에는 대표 source 1개만 렌더링하고, 나머지 화질/원본 후보는 fallbackSources로 보관해 handleError에서 수동 전환한다.
        sources: allSources.slice(0, 1),
        controlBar: this.getControlBarOptions(),
        errorDisplay: false,
        errorCode: 0
      },
      fallbackSources: allSources,
      player: null,
      hasErrorOccurred: false,
      fallbackSourceIndex: 1,
    }
  },
  computed: {
    // 자동 resize 적용: useOriginal이 false이고, resize prop이 없고, src에 '/resize'가 없으면 480 적용
    effectiveResize () {
      // 원본 비디오 사용 옵션이 켜져있으면 resize 적용 안 함
      if (this.useOriginal) {
        return null
      }
      // resize prop이 명시적으로 제공된 경우 그대로 사용
      if (this.resize !== null) {
        return this.resize
      }
      // src에 이미 '/resize'가 포함되어 있으면 resize 적용 안 함
      if (this.src && typeof this.src === 'string' && this.src.includes('/resize')) {
        return null
      }
      // 그 외의 경우 기본값 480 적용
      return 480
    }
  },
  watch: {
    isActive (val) {
      if (val) {
        this.apiPlay()
      } else {
        this.apiPause()
      }
    },
    shouldLoop (val) {
      if (this.player) {
        this.player.loop(val)
      }
    }
  },
  deactivated () {
    // keep-alive에서 비활성화될 때 동영상 정지 + 자원 해제
    this.pauseVideo()
  },
  beforeDestroy () {
    // 컴포넌트 파괴 시 동영상 정지 + 자원 해제 (메모리 누수 방지)
    this.pauseVideo()
  },
  methods: {
    pauseVideo () {
      if (this.player) {
        try {
          this.player.pause()
          this.player.muted(true)
          const videoEl = this.player.el_?.querySelector('video') || this.player.tech_?.el_
          if (videoEl) {
            videoEl.pause()
            videoEl.muted = true
            videoEl.volume = 0
          }
        } catch (e) {
          console.warn('Video pause failed:', e)
        }
      }
    },
    getControlBarOptions () {
      const progressControl = _.isPlainObject(this.options.progressControl)
        ? _.cloneDeep(this.options.progressControl)
        : this.options.progressControl
      const controlBar = _.isPlainObject(this.options.controlBar)
        ? _.cloneDeep(this.options.controlBar)
        : {}

      if (this.options.playToggle !== undefined) {
        controlBar.playToggle = this.options.playToggle
      }
      if (this.options.pictureInPictureToggle !== undefined) {
        controlBar.pictureInPictureToggle = this.options.pictureInPictureToggle
      }
      if (this.options.remainingTimeDisplay !== undefined) {
        controlBar.remainingTimeDisplay = this.options.remainingTimeDisplay
      }
      if (this.options.progressControl !== undefined) {
        controlBar.progressControl = progressControl
      }
      if (this.options.qualitySelector !== undefined) {
        controlBar.qualitySelector = this.options.qualitySelector
      }

      return controlBar
    },
    playerReadied (player) {
      this.player = player
      this.$emit('ready', { player })
      if (this.shouldLoop) {
        player.loop(true)
      }
      if (this.isActive) {
        player.muted(true)
        player.play()
      }
    },
    onVideoEnded () {
      if (this.shouldLoop) {
        this.apiPlay()
      } else {
        this.$emit('ended')
      }
    },
    onPlayerPlay () {
      if (this.useActiveControl && !this.isActive && this.player) {
        this.player.pause()
      }
    },
    load () {
      this.$emit('load')
    },
    play (value) {
      if (this.useMouseEvent) {
        const player = this.$refs[this.videoRef]?.player
        if (!player) {
          return
        }
        if (value) {
          player.currentTime(0)
          player.play()
        } else {
          player.pause()
          player.currentTime(0)
        }
      }
    },
    apiPlay () {
      if (this.player) {
        this.player.muted(true)
        this.player.play()
      }
    },
    apiPause () {
      if (this.player) {
        this.player.pause()
      }
    },
    handleError (e) {
      if (typeof this.src !== 'string') {
        this.hasErrorOccurred = true
        return
      }
      const player = this.$refs[this.videoRef]?.player
      const sources = this.fallbackSources
      if (player && this.fallbackSourceIndex < sources.length) {
        player.src(sources[this.fallbackSourceIndex])
        this.fallbackSourceIndex += 1
        return
      }
      this.hasErrorOccurred = true
      this.$emit('error', e)
    },
    handleContainerClick () {
      if (this.useClickCatcher && this.onVideoClick) {
        this.onVideoClick()
      }
    }
  }
}
